<template>
  <dialog-base
    v-bind="$props"
    :key="renderKey"
    :header="$t('carrier.dialogs.editCarrierDialog.headers.editCarrierContact')"
    :loading="false"
    :scrollable="false"
    @close="close"
    @confirm="submit"
    class="mx-auto"
    has-dialog-actions
    @open="initializeForm"
    max-width="50%"
    width="50%">
    <template v-slot:activator="slotProps" v-if="!externalActivator">
      <slot v-if="$slots.activator" name="activator" v-bind="slotProps"></slot>
    </template>
    <template #body="{ dialogActionsRef }">
      <v-alert prominent type="warning" icon="mdi-alert-octagon-outline" color="error">
        <div class="d-flex flex-row align-center justify-space-between">
          <div>
            <p>
              <strong>
                {{ $t('carrier.dialogs.editCarrierDialog.messages.thisIsASystemWideAction') }}!
              </strong>
            </p>
            <p>
              {{
                $t(
                  'carrier.dialogs.editCarrierDialog.messages.modifyingThisCarrierContactWillAffectAllOpendockOrganizations'
                )
              }}.
            </p>
          </div>

          <v-btn variant="outlined" v-if="!isRiskAcknowledged" @click="isRiskAcknowledged = true">
            {{ $t('carrier.dialogs.editCarrierDialog.buttons.iUnderstand') }}
          </v-btn>
        </div>
      </v-alert>
      <form-base required-legend-class="pl-3" :teleport-target="dialogActionsRef">
        <template #form>
          <v-form ref="form" :disabled="!isRiskAcknowledged">
            <text-field
              :required="true"
              type="text"
              v-model="localCarrier.firstName"
              :rules="
                $validator.rules.required($t('carrier.dialogs.editCarrierDialog.labels.firstName'))
              "
              :label="$t('carrier.dialogs.editCarrierDialog.labels.firstName')"></text-field>
            <text-field
              :required="true"
              type="text"
              v-model="localCarrier.lastName"
              :rules="
                $validator.rules.required($t('carrier.dialogs.editCarrierDialog.labels.lastName'))
              "
              :label="$t('carrier.dialogs.editCarrierDialog.labels.lastName')"></text-field>
            <text-field
              type="text"
              v-model="localCarrier.phone"
              :label="$t('carrier.dialogs.editCarrierDialog.labels.phoneNumber')"></text-field>
            <company-select
              v-if="localCarrier"
              v-model="localCarrier.company"
              ref="companySelect"
              hide-details
              :entry-point="entryPoint"
              :placeholder="
                $t(
                  'carrier.dialogs.editCarrierDialog.placeholders.searchForACompanyNameScacUsdotOrMc'
                )
              "
              :rules="
                $validator.rules.required(
                  $t('carrier.dialogs.editCarrierDialog.labels.carrierCompany')
                )
              "
              hide-icon
              required
              :disabled="!isRiskAcknowledged"></company-select>
            <v-btn
              variant="text"
              size="x-small"
              :disabled="!isRiskAcknowledged"
              @click="openCompanyDialog"
              class="text-secondary font-weight-bold cursor-pointer">
              {{ $t('carrier.dialogs.editCarrierDialog.buttons.createANewCompany') }}
            </v-btn>
          </v-form>
        </template>
        <template #form-actions>
          <action-group
            :disable-confirm="!isRiskAcknowledged || !isFormValid"
            @cancel="close"
            confirm-icon="upload"
            :confirm-label="$t('carrier.dialogs.editCarrierDialog.buttons.updateCarrier')"
            @confirm="submit"></action-group>
        </template>
      </form-base>
    </template>
  </dialog-base>
</template>

<script>
import dialogMixin from '@satellite/components/mixins/dialogMixin';
import { emitCustomEvent } from '@/composables/useCustomEvent';
import { get } from 'lodash';

export default {
  mixins: [dialogMixin],
  name: 'edit-carrier-dialog',
  props: {
    carrier: {
      type: Object,
      required: true
    },
    /*
     * Mixpanel entry point
     */
    entryPoint: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      localCarrier: {},
      isRiskAcknowledged: false
    };
  },
  computed: {
    isFormValid() {
      const firstName = get(this.localCarrier, 'firstName', '');
      const lastName = get(this.localCarrier, 'lastName', '');
      const company = get(this.localCarrier, 'company');

      return firstName && lastName && company;
    }
  },
  methods: {
    handleCarrierSave(carrier) {
      this.$emit('save', carrier);
    },
    openCompanyDialog() {
      emitCustomEvent('open-create-carrier-company-dialog', {
        mixpanelEntryPoint: this.entryPoint,
        onCompanyCreated: this.handleCompanyCreated
      });
    },
    async submit() {
      const id = get(this.localCarrier, 'id', '');
      const firstName = get(this.localCarrier, 'firstName', '');
      const lastName = get(this.localCarrier, 'lastName', '');
      const phone = get(this.localCarrier, 'phone', '-');
      const company = get(this.localCarrier, 'company');

      if (!(await this.$isFormValid(this.$refs.form))) {
        return;
      }

      this.$confirm(
        `${this.$t('carrier.dialogs.editCarrierDialog.labels.firstName')}: ${firstName}<br />
        ${this.$t('carrier.dialogs.editCarrierDialog.labels.lastName')}: ${lastName}<br />
        ${this.$t('carrier.dialogs.editCarrierDialog.labels.phoneNumber')}: ${phone || 'N/A'}<br />
        ${this.$t('carrier.dialogs.editCarrierDialog.labels.carrierCompany')}: ${company.name}`,
        {
          title: this.$t('carrier.dialogs.editCarrierDialog.messages.isThisCorrect'),
          color: 'warning'
        }
      ).then(async confirmed => {
        if (confirmed) {
          const carrierUpdateDto = {
            id,
            companyId: company.id,
            firstName,
            lastName,
            phone
          };

          await this.$store.dispatch('Users/updateUser', carrierUpdateDto);
          this.$emit('save');
          this.close();
        }
      });
    },
    handleCompanyCreated(company) {
      this.localCarrier.companyId = company.id;
      this.$refs.companySelect.handleNewCompany(company);
    },
    initializeForm() {
      this.localCarrier = this.novaCore.deepClone(this.carrier);
      this.isRiskAcknowledged = false;
    }
  }
};
</script>
